import { useState } from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { toast } from 'sonner';
import { Users, TrendingUp, Star, Calendar, MessageSquare, FileText, ChevronRight, Search, Mail, Phone, Trash2 } from 'lucide-react';

const MOCK_CLIENTS = [
  {
    id: 1, name: 'Sophie Martin', email: 'sophie.martin@email.com', phone: '06 12 34 56 78',
    status: 'active', score: 92, staysCount: 2, lastStay: '2026-01-15',
    tags: ['Aidante fille', 'PMR', 'Marrakech'],
    history: [
      { date: '2025-09-10', action: 'Première demande soumise', type: 'request' },
      { date: '2025-10-01', action: 'Séjour confirmé — Riad Marrakech', type: 'booking' },
      { date: '2025-10-20', action: 'Séjour terminé — Note 5/5', type: 'completed' },
      { date: '2026-01-05', action: 'Deuxième demande soumise', type: 'request' },
      { date: '2026-01-15', action: 'Séjour confirmé — Cure Agadir', type: 'booking' },
    ]
  },
  {
    id: 2, name: 'Jean-Pierre Laurent', email: 'jp.laurent@email.com', phone: '07 23 45 67 89',
    status: 'active', score: 78, staysCount: 1, lastStay: '2025-12-05',
    tags: ['Aidant conjoint', 'Alzheimer', 'Agadir'],
    history: [
      { date: '2025-11-01', action: 'Lead éligibilité — score 78%', type: 'lead' },
      { date: '2025-11-15', action: 'Demande soumise', type: 'request' },
      { date: '2025-12-01', action: 'Séjour confirmé — Villa Senior', type: 'booking' },
      { date: '2025-12-05', action: 'Séjour terminé — Note 4/5', type: 'completed' },
    ]
  },
  {
    id: 3, name: 'Marie-Claire Dubois', email: 'mc.dubois@email.com', phone: '06 34 56 78 90',
    status: 'prospect', score: 65, staysCount: 0, lastStay: null,
    tags: ['Aidante mère', 'Handicap moteur', 'Marrakech'],
    history: [
      { date: '2026-01-20', action: 'Lead éligibilité — score 65%', type: 'lead' },
      { date: '2026-01-22', action: 'Contact pris par l\'équipe', type: 'contact' },
    ]
  },
  {
    id: 4, name: 'Ahmed Benali', email: 'ahmed.benali@email.com', phone: '06 45 67 89 01',
    status: 'active', score: 88, staysCount: 3, lastStay: '2026-01-25',
    tags: ['Aidant fils', 'Marrakech', 'Fidèle'],
    history: [
      { date: '2025-06-01', action: 'Première demande', type: 'request' },
      { date: '2025-07-01', action: 'Séjour 1 — terminé 5/5', type: 'completed' },
      { date: '2025-10-01', action: 'Séjour 2 — terminé 5/5', type: 'completed' },
      { date: '2026-01-10', action: 'Séjour 3 — terminé 5/5', type: 'completed' },
    ]
  },
];

const HISTORY_COLORS = { request: '#5A3B31', booking: '#F47B20', completed: '#22863A', lead: '#8B5CF6', contact: '#0EA5E9' };
const HISTORY_ICONS = { request: FileText, booking: Calendar, completed: Star, lead: TrendingUp, contact: MessageSquare };

export default function CRMModule() {
  const [clients, setClients] = useState(MOCK_CLIENTS);
  const [selected, setSelected] = useState(null);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');

  const filtered = clients.filter(c =>
    (filter === 'all' || c.status === filter) &&
    (c.name.toLowerCase().includes(search.toLowerCase()) || c.email.toLowerCase().includes(search.toLowerCase()))
  );

  const removeClient = (id) => {
    const c = clients.find(x => x.id === id);
    if (!window.confirm(`Supprimer le profil de « ${c.name} » ?`)) return;
    if (selected?.id === id) setSelected(null);
    setClients(clients.filter(x => x.id !== id));
    toast.success('Profil supprimé');
  };

  return (
    <div className="p-6 space-y-6" data-testid="crm-module">
      <div>
        <h2 className="font-heading text-2xl font-semibold text-primary">CRM Avancé</h2>
        <p className="text-sm text-muted-foreground font-body">Vue 360° des aidants — parcours complet, score d'engagement</p>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Clients actifs', value: clients.filter(c => c.status === 'active').length, Icon: Users, color: '#22863A' },
          { label: 'Prospects', value: clients.filter(c => c.status === 'prospect').length, Icon: TrendingUp, color: '#8B5CF6' },
          { label: 'Score moyen', value: Math.round(clients.reduce((a, c) => a + c.score, 0) / clients.length), Icon: Star, color: '#F47B20' },
          { label: 'Séjours totaux', value: clients.reduce((a, c) => a + c.staysCount, 0), Icon: Calendar, color: '#5A3B31' },
        ].map(({ label, value, Icon, color }) => (
          <div key={label} className="bg-white rounded-2xl p-5 border border-border shadow-sm flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: color + '18' }}>
              <Icon className="w-5 h-5" style={{ color }} />
            </div>
            <div>
              <p className="font-heading text-xl font-semibold text-primary">{value}</p>
              <p className="text-xs text-muted-foreground font-body">{label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Search + filter */}
      <div className="flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Rechercher un aidant…" className="pl-9 font-body rounded-full" />
        </div>
        {[['all', 'Tous'], ['active', 'Actifs'], ['prospect', 'Prospects']].map(([k, l]) => (
          <button key={k} onClick={() => setFilter(k)} className={`px-3 py-1.5 rounded-full text-xs font-body font-medium transition-all ${filter === k ? 'bg-primary text-white' : 'bg-muted text-foreground/70 hover:bg-muted/80'}`}>{l}</button>
        ))}
      </div>

      {/* Layout: list + detail */}
      <div className="flex gap-4">
        {/* Client list */}
        <div className={`space-y-2 ${selected ? 'w-80 flex-shrink-0' : 'w-full'}`}>
          {filtered.map(c => (
            <div
              key={c.id}
              className={`bg-white rounded-2xl border shadow-sm p-4 transition-all ${selected?.id === c.id ? 'border-primary/60 ring-1 ring-primary/20' : 'border-border hover:border-primary/30'}`}
              data-testid={`crm-client-${c.id}`}
            >
              <div className="flex items-center gap-3">
                {/* Avatar clickable */}
                <div
                  onClick={() => setSelected(c)}
                  className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-sm font-bold text-primary flex-shrink-0 cursor-pointer"
                >
                  {c.name.split(' ').map(n => n[0]).join('')}
                </div>
                <div className="flex-1 min-w-0 cursor-pointer" onClick={() => setSelected(c)}>
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="font-heading font-semibold text-primary text-sm truncate">{c.name}</span>
                    <Badge className={c.status === 'active' ? 'bg-green-100 text-green-700 text-xs flex-shrink-0' : 'bg-purple-100 text-purple-700 text-xs flex-shrink-0'}>
                      {c.status === 'active' ? 'Client' : 'Prospect'}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 bg-muted rounded-full h-1.5">
                      <div className="h-1.5 rounded-full" style={{ width: `${c.score}%`, background: c.score >= 80 ? '#22863A' : c.score >= 60 ? '#F47B20' : '#B04730' }} />
                    </div>
                    <span className="text-xs font-body text-muted-foreground flex-shrink-0">{c.score}/100</span>
                  </div>
                  <p className="text-xs text-muted-foreground font-body mt-0.5 truncate">{c.email}</p>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <button onClick={() => setSelected(c)} className="p-1.5 hover:bg-muted rounded-lg transition-colors">
                    <ChevronRight className={`w-4 h-4 text-muted-foreground transition-transform ${selected?.id === c.id ? 'rotate-90' : ''}`} />
                  </button>
                  <button onClick={() => removeClient(c.id)} className="p-1.5 hover:bg-red-50 rounded-lg transition-colors" title="Supprimer" data-testid={`crm-delete-${c.id}`}>
                    <Trash2 className="w-3.5 h-3.5 text-red-400 hover:text-red-600" />
                  </button>
                </div>
              </div>
            </div>
          ))}
          {filtered.length === 0 && (
            <div className="text-center py-12 text-muted-foreground font-body text-sm">Aucun client trouvé</div>
          )}
        </div>

        {/* Client detail */}
        {selected && (
          <div className="flex-1 bg-white rounded-2xl border border-border shadow-sm p-5 space-y-5" data-testid="crm-client-detail">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="font-heading text-lg font-semibold text-primary">{selected.name}</h3>
                <p className="text-xs text-muted-foreground font-body">{selected.email}</p>
              </div>
              <button onClick={() => setSelected(null)} className="p-1.5 hover:bg-muted rounded-lg text-muted-foreground transition-colors">
                <ChevronRight className="w-4 h-4 rotate-180" />
              </button>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="bg-background rounded-xl p-3 border border-border">
                <p className="text-xs text-muted-foreground font-body mb-0.5">Score d'engagement</p>
                <p className="font-heading text-xl font-semibold text-primary">{selected.score}/100</p>
              </div>
              <div className="bg-background rounded-xl p-3 border border-border">
                <p className="text-xs text-muted-foreground font-body mb-0.5">Séjours</p>
                <p className="font-heading text-xl font-semibold text-primary">{selected.staysCount}</p>
              </div>
            </div>

            <div className="flex gap-2 flex-wrap">
              {selected.tags.map(tag => (
                <span key={tag} className="text-xs px-2 py-1 rounded-full bg-[#FFF3E8] text-[#F47B20] font-body font-medium">{tag}</span>
              ))}
            </div>

            <div className="flex gap-2">
              <Button size="sm" variant="outline" className="rounded-full text-xs font-body gap-1 flex-1" onClick={() => toast.success('Email ouvert')}>
                <Mail className="w-3 h-3" /> Email
              </Button>
              <Button size="sm" variant="outline" className="rounded-full text-xs font-body gap-1 flex-1" onClick={() => toast.success('Appel initié')}>
                <Phone className="w-3 h-3" /> Appeler
              </Button>
            </div>

            <div>
              <h4 className="font-heading font-semibold text-sm text-primary mb-3">Historique</h4>
              <div className="space-y-3 max-h-56 overflow-y-auto pr-1">
                {selected.history.slice().reverse().map((event, i) => {
                  const Icon = HISTORY_ICONS[event.type] || FileText;
                  const color = HISTORY_COLORS[event.type] || '#5A3B31';
                  return (
                    <div key={i} className="flex items-start gap-3">
                      <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: color + '18' }}>
                        <Icon className="w-3.5 h-3.5" style={{ color }} />
                      </div>
                      <div>
                        <p className="text-xs font-body text-primary">{event.action}</p>
                        <p className="text-xs text-muted-foreground font-body">{new Date(event.date).toLocaleDateString('fr-FR')}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
